import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import DefaultTable from '../../components/DefaultTable';
import PageWrapper from '../../components/PageWrapper';
import SaveBox from '../../components/SaveBox';

import { requestTransferDrawerColumns } from '../../table-configs/columns-schemas';
import { searchCompanyNameAndDocument } from 'app/store/slices/company';
import helpers from 'app/utils/helpers';
import { STATUS_COLORS } from 'app/config/status';
import { fetchRequests } from 'app/store/slices/requests';
import { errorHandler } from 'app/utils/errorHandler';
import { CircularProgress, FilledInput, FormControl, Grid, InputAdornment, InputLabel } from '@material-ui/core';
import Search from '@material-ui/icons/Search';

const FindRequest = ({ onClose, onSave, filters, hasSearch }) => {
    const dispatch = useDispatch();

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(5);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const [selectedId, setSelectedId] = useState(null);
    const [searchTxt, setSearchTxt] = useState('');
    const [txtBusca, setTxtBusca] = useState('');

    const requests = useSelector((state) => state.requests.requests);

    const handleSelectRequest = (param) => {
        const { data } = param;
        if (data?.originalData) {
            const temp = { ...data.originalData };
            // delete temp.jsonSolicitacao;
            setSelected(temp);
            setSelectedId(temp.cdSolicitacao);
        }
    };

    const handleSaveSelected = () => {
        if (onSave) onSave(selected);
        onClose();
    };

    const handleTextSearch = (onBlur) => {
        if (onBlur) {
            if (searchTxt !== '') return;
        }
        if (searchTxt.indexOf('.') === 2 || searchTxt.indexOf('/') === 10) {
            setTxtBusca(helpers.somenteAlfanumericos(searchTxt));
        } else {
            setTxtBusca(searchTxt);
        }
        setPage(0);
    };

    const handleChangePageSize = (e) => {
        setPage(0);
        setPageSize(parseInt(e.target.value));
    };

    const handleChangePage = (e, page) => {
        setPage(page);
    };

    const prepareData = useCallback(async (content) => {
        let document = '';
        let name = '';
        if (content.jsonSolicitacao) {
            const jsonSolicitacao = JSON.parse(content.jsonSolicitacao);
            document = jsonSolicitacao.cdTipoPessoaFisica
                ? jsonSolicitacao.deCpf || ''
                : jsonSolicitacao.deCnpjMatriz || jsonSolicitacao.deCpfCnpj || '';
            name = jsonSolicitacao.cdTipoPessoaFisica
                ? jsonSolicitacao.nmCompleto || ''
                : jsonSolicitacao.deRazaoSocial || jsonSolicitacao.nmCompleto || jsonSolicitacao.nmUsuario || '';
            if ((document === '' || name === '') && content.cdSolicitacao) {
                const companyInfo = await searchCompanyNameAndDocument(content.cdSolicitacao);
                name = companyInfo.name;
                document = companyInfo.document;
            }
        }
        return {
            originalData: content,
            cdSolicitacao: content.cdSolicitacao,
            dtCadastro: (
                <>
                    <div>{helpers.formatarData(content.dtCadastro, true)}</div>
                    {content.servico && (
                        <div>
                            <strong>{content.servico.nmServico}</strong>
                        </div>
                    )}
                </>
            ),
            cdEmpresa: (
                <>
                    {document !== '' && <div>{helpers.formatarDocumento(document)}</div>}
                    {name !== '' && (
                        <div>
                            <strong>{name}</strong>
                        </div>
                    )}
                </>
            ),
            nuSolicitacao: (
                <>
                    {content.nuSolicitacaoFmt && <div>{content.nuSolicitacaoFmt}</div>}
                    {content.nuProcesso && <div>{content.nuProcesso}</div>}
                </>
            ),
            deSituacao: (
                <>
                    {content.situacao && (
                        <div style={{ color: `${STATUS_COLORS[content.situacao.cdSituacao]?.color}` }}>
                            <strong>{content.situacao.deSituacao}</strong>
                        </div>
                    )}
                </>
            ),
        };
    }, []);

    useEffect(() => {
        const findRequests = async () => {
            try {
                const promises = [];
                const search = { ...filters, page: page, size: pageSize };
                if (txtBusca !== '') {
                    search.txtBusca = txtBusca;
                }
                promises.push(dispatch(fetchRequests(search, '')));
                await Promise.all(promises);
            } catch (error) {
                errorHandler(error.response?.status, error.response?.data?.message);
            }
        };
        findRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, filters, txtBusca, page, pageSize]);

    useEffect(() => {
        const init = async () => {
            const rowsTemp = [];
            if (requests.content) {
                setLoading(true);
                for (const cont of requests.content) {
                    const rowsPrepared = await prepareData(cont);
                    rowsTemp.push(rowsPrepared);
                }
                setLoading(false);
            }
            setRows(rowsTemp);
        };
        init();
    }, [prepareData, requests, setLoading]);

    return (
        <PageWrapper
            maxWidth="md"
            title="Selecionar solicitação"
            isPlain
            actionButton={
                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            }>
            {hasSearch && (
                <>
                    <Grid container>
                        <Grid item xs={9}>
                            {loading && (
                                <Grid item xs={12} style={{ textAlign: 'right' }}>
                                    <CircularProgress size={16} style={{ margin: '15px 20px 0 0' }} />
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={3}>
                            <FormControl size="small" variant="filled" fullWidth>
                                <InputLabel>Buscar</InputLabel>
                                <FilledInput
                                    value={searchTxt}
                                    onChange={(e) => setSearchTxt(e.target.value)}
                                    endAdornment={
                                        <InputAdornment
                                            position="end"
                                            onClick={() => handleTextSearch()}
                                            style={{ cursor: 'pointer' }}>
                                            <Search />
                                        </InputAdornment>
                                    }
                                    aria-describedby="outlined-weight-helper-text"
                                    inputProps={{
                                        'aria-label': 'weight',
                                    }}
                                    onBlur={() => {
                                        handleTextSearch(true);
                                    }}
                                    onKeyPress={(ev) => {
                                        if (ev.key === 'Enter') {
                                            ev.preventDefault();
                                            handleTextSearch();
                                        }
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </Grid>
                    <br />
                </>
            )}

            <DefaultTable
                columns={requestTransferDrawerColumns}
                rows={rows}
                loading={loading}
                entryProps={{
                    onChangeSelect: handleSelectRequest,
                    selectedId,
                }}
                paginationProps={{
                    rowsPerPageOptions: [5, 10, 25],
                    count: requests?.totalElements || 0,
                    rowsPerPage: pageSize,
                    page: page,
                    onPageChange: handleChangePage,
                    onRowsPerPageChange: handleChangePageSize,
                }}
            />

            <SaveBox
                firstButtonLabel={'Voltar'}
                middleButtonLabel="Selecionar"
                onSave={handleSaveSelected}
                onCancel={onClose}
            />
        </PageWrapper>
    );
};

export default FindRequest;
